/**
 * @fileoverview One notification row in the inbox list (MOR-716).
 *
 * Columns: event-type icon · actor avatar · (task, in feed view) · full
 * wrapping description with coloured status badges · time · hover actions
 * (done / snooze / read). Clicking the body opens the task.
 */

import { formatDistanceToNow } from "date-fns";
import {
  BellRing,
  CalendarDays,
  Check,
  CheckCircle2,
  Clock,
  Flag,
  Link2,
  ListTree,
  Mail,
  MailOpen,
  MessageSquare,
  Pencil,
  Plus,
  RotateCcw,
  Shapes,
  Tag,
  UserCheck,
  UserPlus,
  UserMinus,
  Zap,
  type LucideIcon,
} from "lucide-react";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { useMutation } from "convex/react";
import type { EventPayload } from "@cvx/notifications/describe";
import type { TaskEventType } from "@cvx/notifications/prefs";
import type { KeyDateCategory } from "@cvx/keyDates/jobKeyDates/schema";
import { Badge } from "@/ui/primitives/badge";
import { Button } from "@/ui/primitives/button";
import { Checkbox } from "@/ui/primitives/checkbox";
import { UserAvatar } from "@/ui/composed";
import { CATEGORY_META } from "@/calendar/categoryMeta";
import { priorityMeta } from "@/calendar/task-priority";
import { cn } from "@/utils/misc";
import { SnoozeMenu } from "./snooze-menu";

/** Row type: task events plus automation notices/reminders (MOR-736). */
export type InboxRowType =
  TaskEventType | "automation_notice" | "automation_reminder";

export type InboxRow = {
  _id: Id<"notifications">;
  /** Absent for automation notices not tied to a task (MOR-736). */
  taskId?: Id<"jobKeyDates">;
  jobId: Id<"jobs">;
  type: InboxRowType;
  status: "unread" | "read" | "done";
  snoozedUntil?: number;
  createdAt: number;
  taskTitle: string;
  jobName: string;
  actorName: string;
  actorFirstName?: string;
  actorLastName?: string;
  actorEmail?: string;
  actorAvatarUrl?: string;
  description: string;
  payload?: EventPayload;
};

/** Type guard for whether a raw string is a known `KeyDateCategory`. */
function isKeyDateCategory(value: string): value is KeyDateCategory {
  return Object.prototype.hasOwnProperty.call(CATEGORY_META, value);
}

/** Icon per event type, refined by which fields changed — mirrors the task
 * panel's row icons (Status/Category/Priority/Assignee/Parent/Labels). */
function eventIcon(type: InboxRowType, payload?: EventPayload): LucideIcon {
  switch (type) {
    case "automation_notice":
      return Zap;
    case "automation_reminder":
      return BellRing;
    case "created":
      return Plus;
    case "assigned":
      return UserPlus;
    case "unassigned":
      return UserMinus;
    case "status_changed":
      return CheckCircle2;
    case "completed":
      return CheckCircle2;
    case "reopened":
      return RotateCcw;
    case "comment_added":
      return MessageSquare;
    case "priority_changed":
      return Flag;
    case "dates_changed": {
      const fields = payload?.fields ?? [];
      const timeOnly =
        fields.length > 0 && fields.every((f) => /time|allday/i.test(f));
      return timeOnly ? Clock : CalendarDays;
    }
    case "details_changed": {
      const fields = payload?.fields ?? [];
      if (fields.length === 1) {
        if (fields[0] === "labelIds") return Tag;
        if (fields[0] === "category") return Shapes;
        if (fields[0] === "parentTaskId") return ListTree;
        if (fields[0] === "assignorUserId") return UserCheck;
      }
      return Pencil;
    }
    case "dependency_added":
    case "dependency_removed":
      return Link2;
  }
}

const STATUS_EVENT_LABEL: Partial<Record<InboxRowType, string>> = {
  status_changed: "Status changed",
  completed: "Marked as",
  reopened: "Reopened as",
};

/**
 * Description cell — renders the app's real UI elements for what changed:
 * status/label badges, the category chip, the priority icon; text otherwise.
 */
function RowDescription({ row }: { row: InboxRow }) {
  const p = row.payload;
  const statusLabel = STATUS_EVENT_LABEL[row.type];

  if (statusLabel && p?.statusName) {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <span>{statusLabel}</span>
        {row.type === "status_changed" && p.previousStatusName && (
          <>
            <Badge color={p.previousStatusColor ?? "gray"} variant="soft">
              {p.previousStatusName}
            </Badge>
            <span className="text-muted-foreground">→</span>
          </>
        )}
        <Badge color={p.statusColor ?? "gray"} variant="soft">
          {p.statusName}
        </Badge>
      </span>
    );
  }

  // Label changes: real label badges, added plain, removed struck out.
  if (
    row.type === "details_changed" &&
    ((p?.labelsAdded?.length ?? 0) > 0 || (p?.labelsRemoved?.length ?? 0) > 0)
  ) {
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <span>Labels</span>
        {(p?.labelsAdded ?? []).map((l) => (
          <Badge key={`+${l.name}`} color={l.color} variant="soft">
            {l.name}
          </Badge>
        ))}
        {(p?.labelsRemoved ?? []).map((l) => (
          <Badge
            key={`-${l.name}`}
            color={l.color}
            variant="soft"
            className="line-through opacity-60"
          >
            {l.name}
          </Badge>
        ))}
      </span>
    );
  }

  // Category changes: the same coloured category chip the task panel uses.
  if (
    row.type === "details_changed" &&
    p?.fields?.length === 1 &&
    p.fields[0] === "category"
  ) {
    const key =
      p.next && typeof p.next === "object"
        ? (p.next as Record<string, unknown>).category
        : undefined;
    const meta =
      typeof key === "string" && isKeyDateCategory(key)
        ? CATEGORY_META[key]
        : undefined;
    if (meta) {
      const MetaIcon = meta.icon;
      return (
        <span className="inline-flex flex-wrap items-center gap-1.5">
          <span>Category changed to</span>
          <Badge
            variant="outline"
            className={`gap-1 ${meta.iconBgClass} ${meta.iconFgClass} border-transparent`}
          >
            <MetaIcon className="size-3" />
            {meta.label}
          </Badge>
        </span>
      );
    }
  }

  // Priority changes: the priority icon + name, matching the task panel.
  if (row.type === "priority_changed" && typeof p?.next === "number") {
    const meta = priorityMeta(p.next);
    const MetaIcon = meta.icon;
    return (
      <span className="inline-flex flex-wrap items-center gap-1.5">
        <span>Priority changed to</span>
        <Badge color={meta.color} variant="soft" className="gap-1">
          <MetaIcon className="size-3" />
          {meta.label}
        </Badge>
      </span>
    );
  }

  // Comments: the snippet on its own muted line under the lead-in.
  if (row.type === "comment_added" && p?.snippet) {
    return (
      <span className="block">
        <span>New comment</span>
        <span className="mt-0.5 block border-l-2 border-border pl-2 text-[13px] font-normal italic text-muted-foreground">
          {p.snippet}
        </span>
      </span>
    );
  }

  return <span>{row.description}</span>;
}

export function NotificationRow({
  row,
  tab,
  showTask = false,
  selected = false,
  selectionActive = false,
  onToggleSelect,
  onOpen,
}: {
  row: InboxRow;
  tab: "inbox" | "snoozed" | "done";
  /** Feed view: show the task title + job as a leading column. */
  showTask?: boolean;
  /** Bulk selection (MOR-716): checkbox column, shown on hover or while
   * any row is selected. */
  selected?: boolean;
  selectionActive?: boolean;
  onToggleSelect?: (id: Id<"notifications">) => void;
  onOpen: (taskId: Id<"jobKeyDates">) => void;
}) {
  const markRead = useMutation(api.notifications.inbox.markRead);
  const markUnread = useMutation(api.notifications.inbox.markUnread);
  const markDone = useMutation(api.notifications.inbox.markDone);
  const snooze = useMutation(api.notifications.inbox.snooze);
  const unsnooze = useMutation(api.notifications.inbox.unsnooze);

  const unread = row.status === "unread";
  const Icon = eventIcon(row.type, row.payload);

  return (
    <div
      className={cn(
        "group flex items-start gap-3 rounded-md px-3 py-2 hover:bg-muted/60",
        selected
          ? "bg-primary/10"
          : unread && tab === "inbox"
            ? "bg-primary/5"
            : "bg-transparent",
      )}
    >
      {onToggleSelect && (
        <span
          className={cn(
            "mt-1 shrink-0 transition-opacity",
            selectionActive
              ? "opacity-100"
              : "opacity-0 group-hover:opacity-100",
          )}
        >
          <Checkbox
            checked={selected}
            onCheckedChange={() => onToggleSelect(row._id)}
            aria-label="Select notification"
          />
        </span>
      )}
      <span
        className={cn(
          "mt-2 size-2 shrink-0 rounded-full",
          unread && tab !== "done" ? "bg-primary" : "bg-transparent",
        )}
      />
      <Icon className="mt-1.5 size-4 shrink-0 text-muted-foreground" />
      <UserAvatar
        avatarUrl={row.actorAvatarUrl}
        firstName={row.actorFirstName}
        lastName={row.actorLastName}
        email={row.actorEmail}
        className="mt-0.5 size-6 shrink-0"
        fallbackClassName="text-[10px]"
      />
      <button
        type="button"
        className="flex min-w-0 flex-1 items-start gap-3 text-left"
        onClick={() => row.taskId && onOpen(row.taskId)}
      >
        {showTask && (
          <span className="w-56 shrink-0 pt-0.5">
            <span
              className={cn(
                "block truncate text-sm",
                unread
                  ? "font-semibold text-foreground"
                  : "font-medium text-foreground/80",
              )}
            >
              {row.taskTitle}
            </span>
            <span className="block truncate text-xs text-muted-foreground">
              {row.jobName}
            </span>
          </span>
        )}
        <span className="min-w-0 flex-1 pt-0.5">
          <span
            className={cn(
              "block text-sm leading-relaxed",
              unread ? "font-medium text-foreground" : "text-foreground/80",
            )}
          >
            <RowDescription row={row} />
          </span>
          <span className="block pt-0.5 text-xs text-muted-foreground">
            {row.actorName}
          </span>
        </span>
      </button>
      <span className="shrink-0 pt-1 text-xs text-muted-foreground">
        {tab === "snoozed" && row.snoozedUntil
          ? `until ${new Date(row.snoozedUntil).toLocaleString(undefined, {
              weekday: "short",
              hour: "numeric",
              minute: "2-digit",
            })}`
          : formatDistanceToNow(row.createdAt, { addSuffix: true })}
      </span>
      <div className="flex shrink-0 items-center gap-0.5 pt-0.5 opacity-0 transition-opacity group-hover:opacity-100">
        {tab !== "done" && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label="Mark done"
            onClick={() => void markDone({ notificationId: row._id })}
          >
            <Check className="size-3.5" />
          </Button>
        )}
        {tab === "inbox" && (
          <SnoozeMenu
            onSnooze={(until) =>
              void snooze({ notificationId: row._id, until })
            }
          />
        )}
        {tab === "snoozed" && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label="Unsnooze"
            onClick={() => void unsnooze({ notificationId: row._id })}
          >
            <Mail className="size-3.5" />
          </Button>
        )}
        {tab === "inbox" && (
          <Button
            variant="ghost"
            size="icon"
            className="size-7"
            aria-label={unread ? "Mark read" : "Mark unread"}
            onClick={() =>
              void (unread
                ? markRead({ notificationId: row._id })
                : markUnread({ notificationId: row._id }))
            }
          >
            {unread ? (
              <MailOpen className="size-3.5" />
            ) : (
              <Mail className="size-3.5" />
            )}
          </Button>
        )}
      </div>
    </div>
  );
}
